import React, {useCallback} from 'react';
import {getMediaUrl} from '@wix/wixstores-client-core/dist/es/src/media/mediaService';
import {SocialShareIconsTheme, SocialVendor} from '../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../providers/globalPropsProvider';
import {
  FacebookSocialNetwork,
  PinterestSocialNetwork,
  SocialShareService,
  TwitterSocialNetwork,
  WhatsappSocialNetwork,
} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/ProductSocialShare/SocialShareService';
import style from './ProductSocial.scss';
import {getDefaultProductMedia, getVenderStyleParam} from './ProductSocialUtils';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {ProductSocialOld} from './ProductSocialOld';
import {SocialBar} from '@wix/wix-ui-tpa';
import {Facebook, Pinterest, Whatsapp, XCorp} from '@wix/wix-ui-icons-common/on-stage';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../specs';
import {pathgen} from 'object-path-generator';
import {useStyles} from '@wix/tpa-settings/react';
import stylesParams from '../../../components/ProductPage/stylesParams';
import {ConditionalRender} from '../ConditionalRender/ConditionalRender';

export interface ProductSocialProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  isLiveSiteMode?: boolean;
}

export const ProductSocialDataHooks = pathgen<{
  SocialSharing: string;
  SocialIcon(type: SocialVendor): string;
}>('ProductSocialDataHooks');

const socialNetworks = {
  [SocialVendor.Facebook]: FacebookSocialNetwork,
  [SocialVendor.Pinterest]: PinterestSocialNetwork,
  [SocialVendor.WhatsApp]: WhatsappSocialNetwork,
  [SocialVendor.Twitter]: TwitterSocialNetwork,
};

const ProductSocialInternal: React.FC<ProductSocialProps> = ({globals, t}) => {
  const styles = useStyles();

  const socialShareLinkByVendor = (shareType: SocialVendor) => {
    const {product, productUrl} = globals;
    const vendor = new socialNetworks[shareType]();
    const productImage = getDefaultProductMedia(product);
    const options = {asJpg: false, isSEOBot: true};
    const imageUrl = getMediaUrl(productImage, productImage, options);
    return new SocialShareService(vendor).getSocialNetworkUrl({
      title: product.name,
      url: productUrl,
      imageUrl,
    });
  };

  const onClick = (socialVendor: SocialVendor) => {
    globals.socialSharing.onClick({socialButton: socialVendor});
    const socialShareUrl = socialShareLinkByVendor(socialVendor);

    window.open(socialShareUrl, '_blank', 'width=700, height=600, top=300, left=300, chrome=yes, centerscreen');
  };

  const socialIconsTheme = styles.get(stylesParams.productPage_socialShareIconsTheme) as SocialShareIconsTheme;
  const isOriginalTheme = socialIconsTheme === SocialShareIconsTheme.ORIGINAL;
  const nonOriginalColor = socialIconsTheme === SocialShareIconsTheme.LIGHT ? '#ffffff' : '#000000';
  const socialIconByVendor = {
    [SocialVendor.Facebook]: <Facebook style={{color: isOriginalTheme ? '#107FEA' : nonOriginalColor}} />,
    [SocialVendor.Pinterest]: <Pinterest style={{color: isOriginalTheme ? '#E81627' : nonOriginalColor}} />,
    [SocialVendor.WhatsApp]: <Whatsapp style={{color: isOriginalTheme ? '#1BC149' : nonOriginalColor}} />,
    [SocialVendor.Twitter]: <XCorp style={{color: isOriginalTheme ? '#000000' : nonOriginalColor}} />,
  };

  const VendorSocialIcon = ({vendor}: {vendor: SocialVendor}) => {
    /* istanbul ignore next */
    const memoizedOnClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onClick(vendor);
      },
      [vendor]
    );
    const ariaLabel = vendor === SocialVendor.Twitter ? t('SHARE_TITLE_X') : t(`SHARE_TITLE_${vendor.toUpperCase()}`);
    const socialShareUrl = socialShareLinkByVendor(vendor);

    return (
      <SocialBar.Icon
        data-hook={ProductSocialDataHooks.SocialIcon(vendor)}
        aria-label={ariaLabel}
        /* istanbul ignore next */
        onClick={memoizedOnClick}
        icon={socialIconByVendor[vendor]}
        href={socialShareUrl}
      />
    );
  };

  return (
    <SocialBar data-hook={ProductSocialDataHooks.SocialSharing()} className={style.root}>
      {Object.keys(socialNetworks).map((vendor: SocialVendor) => {
        const venderStyleParam = getVenderStyleParam(vendor);

        return (
          <ConditionalRender by={venderStyleParam} className={style.icon} key={`social-sharing-${vendor}`}>
            <VendorSocialIcon vendor={vendor} />
          </ConditionalRender>
        );
      })}
    </SocialBar>
  );
};

const ProductSocialExperiment: React.FC<ProductSocialProps> = (props) => {
  const {experiments} = useExperiments();
  if (experiments.enabled(SPECS.ShouldProductPageUseNewSocialIcons)) {
    return <ProductSocialInternal {...props} />;
  } else {
    return <ProductSocialOld {...props} />;
  }
};

export const ProductSocial: React.FC<ProductSocialProps> = withGlobalProps(
  withTranslations('globals.texts')(ProductSocialExperiment)
);
